/**
 * One-Euro filter (Casiez, Roussel, Vogel — CHI 2012).
 * Adaptive low-pass: low jitter when still, low lag when moving.
 */
export interface OneEuroParams {
  /** Minimum cutoff frequency (Hz). Lower = smoother when still. */
  minCutoff: number
  /** Speed coefficient. Higher = less lag when moving. */
  beta: number
  /** Cutoff for the derivative low-pass (Hz). */
  dCutoff: number
}

export const ONE_EURO_DEFAULTS: OneEuroParams = { minCutoff: 1.0, beta: 0.0, dCutoff: 1.0 }

function smoothingFactor(dt: number, cutoff: number): number {
  const r = 2 * Math.PI * cutoff * dt
  return r / (r + 1)
}

class LowPass {
  private y = 0
  private initialized = false
  filter(x: number, alpha: number): number {
    if (!this.initialized) {
      this.initialized = true
      this.y = x
      return x
    }
    this.y = alpha * x + (1 - alpha) * this.y
    return this.y
  }
  last(): number {
    return this.y
  }
  hasValue(): boolean {
    return this.initialized
  }
  reset(): void {
    this.initialized = false
    this.y = 0
  }
}

export class OneEuroFilter {
  readonly params: OneEuroParams
  private x = new LowPass()
  private dx = new LowPass()
  private lastT: number | null = null

  constructor(params: Partial<OneEuroParams> = {}) {
    this.params = { ...ONE_EURO_DEFAULTS, ...params }
  }

  /**
   * @param value raw sample
   * @param tSeconds timestamp in seconds
   */
  filter(value: number, tSeconds: number): number {
    if (this.lastT === null || !this.x.hasValue()) {
      this.lastT = tSeconds
      this.dx.filter(0, 1)
      return this.x.filter(value, 1)
    }
    let dt = tSeconds - this.lastT
    if (!(dt > 0)) dt = 1 / 60 // duplicate / out-of-order timestamps: assume a nominal frame
    this.lastT = tSeconds
    const prev = this.x.last()
    const rawD = (value - prev) / dt
    const edx = this.dx.filter(rawD, smoothingFactor(dt, this.params.dCutoff))
    const cutoff = this.params.minCutoff + this.params.beta * Math.abs(edx)
    return this.x.filter(value, smoothingFactor(dt, cutoff))
  }

  /** Last filtered value (NaN if never fed). */
  get value(): number {
    return this.x.hasValue() ? this.x.last() : NaN
  }

  reset(): void {
    this.x.reset()
    this.dx.reset()
    this.lastT = null
  }
}

/** N independent One-Euro filters sharing the same timing (for vectors). */
export class OneEuroVector {
  private filters: OneEuroFilter[]
  constructor(size: number, params: Partial<OneEuroParams> = {}) {
    this.filters = Array.from({ length: size }, () => new OneEuroFilter(params))
  }
  filter<T extends ArrayLike<number> & { [i: number]: number }>(out: T, values: ArrayLike<number>, tSeconds: number): T {
    for (let i = 0; i < this.filters.length; i++) out[i] = this.filters[i]!.filter(values[i]!, tSeconds)
    return out
  }
  reset(): void {
    for (const f of this.filters) f.reset()
  }
}
